// Setup › Recall (`/c/{slug}/setup/recall`; M7-04; managers, the `memory` add-on): how this
// company's memory reaches Recall. One call, `recall/status`: the connection (Recall runs on the
// VPS; without its partner key the server says "not connected" and nothing else is pretended),
// the company's tenant and install token, the callback's registration (one operator step per
// company, 07 gap 2), the agent keys CompanyOS holds, and how many members and buckets are
// linked. No secret is ever shown: keys are "held" or not.
// SETUP-UX: the 56 px location header, then one `SettingsGroup` per part with a row per fact.
// RECALL-KB: a "Knowledge bases" group: each space's "Sync to Recall" switch, the agents' access
// and the last sync (files, when, what was sent, Recall's last refusal).
// D15 (preview): an "Agent memory writes" group: the memory discipline switch (off by default)
// and its per-run write budget, saved in the company's settings.
// RECALL-RULES / RECALL-UPKEEP: "Standing rules" and "Memory upkeep" (recall-upkeep.tsx).
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  NativeSelect,
  NativeSelectOption,
  SettingsGroup,
  SettingsRow,
  Switch,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { companyQuery, keys } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { SetupPage } from "../settings/setup-page";
import {
  memoryKeys,
  type RecallKbSpace,
  type RecallStatus,
  recallKbQuery,
  recallStatusQuery,
} from "./memory-queries";
import { RolesPanel } from "./people-cards";
import { MemoryUpkeep, StandingRules } from "./recall-upkeep";

const CONNECTION: Record<
  RecallStatus["recall"]["state"],
  [string, "success" | "warning" | "neutral" | "danger"]
> = {
  connected: ["Connected", "success"],
  not_configured: ["Not connected", "neutral"],
  not_provisioned: ["Setting up", "warning"],
  unavailable: ["Not answering", "danger"],
};

const KEY_STATE: Record<RecallStatus["agents"][number]["key_state"], string> = {
  none: "Not issued",
  pending: "Issued, not yet confirmed",
  acked: "Held",
  voided: "Voided",
};

export function RecallScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const q = useQuery(recallStatusQuery(api, company.id));
  return (
    <SetupPage title="Recall">
      {q.isPending ? (
        <Loading label="Loading Recall status" rows={4} />
      ) : q.isError ? (
        <LoadError what="Recall status" error={q.error} />
      ) : (
        <>
          <RecallStatusView status={q.data} />
          <KnowledgeBases api={api} co={company.id} />
          <AgentDiscipline api={api} co={company.id} />
          <StandingRules api={api} company={company} />
          <MemoryUpkeep api={api} company={company} />
          <RolesPanel api={api} co={company.id} />
        </>
      )}
    </SetupPage>
  );
}

// WIKI-IN-COMPANYOS: docs live in CompanyOS and agents read them there (search, read_doc); this
// group is the optional copy of a space's docs in Recall, so the labels speak of the index.
const ACCESS: Record<RecallKbSpace["agent_access"], string> = {
  none: "Index only, no agent access",
  read: "Agents may search the index",
  read_write: "Agents may search and write the index",
};

/** RECALL-KB: what the row says under the space's name. */
function kbHint(k: RecallKbSpace, interval: number): string {
  const pages = `${k.pages} ${k.pages === 1 ? "page" : "pages"}`;
  if (!k.sync_enabled) return `${pages} · off${k.opt_out_reason ? `: ${k.opt_out_reason}` : ""}`;
  if (!k.last_synced_at)
    return `${pages} · first sync within a minute${k.sync_requested ? "" : ` (then every ${interval} min at most)`}`;
  const put = k.last_put.length;
  return `${k.files} ${k.files === 1 ? "file" : "files"} in Recall · last synced ${formatDateTime(k.last_synced_at)} · ${put} sent${k.sync_requested ? " · sync asked for" : ""}`;
}

/**
 * RECALL-KB: one row per space. "Sync to Recall" makes the space's pages a Recall knowledge base
 * (markdown files, synced by the worker in one batch at most every `interval_minutes`), and the
 * agent access says what the DEs whose seat reads the space may do in it.
 */
function KnowledgeBases({ api, co }: { api: Api; co: string }) {
  const qc = useQueryClient();
  const q = useQuery(recallKbQuery(api, co));
  const done = () => void qc.invalidateQueries({ queryKey: memoryKeys.kb(co) });
  const update = useMutation({
    mutationFn: (v: {
      id: string;
      sync_enabled: boolean;
      agent_access: RecallKbSpace["agent_access"];
      opt_out_reason?: string;
    }) =>
      api.client.recall.kbUpdate({
        params: { co, id: v.id },
        body: {
          sync_enabled: v.sync_enabled,
          agent_access: v.agent_access,
          ...(v.opt_out_reason ? { opt_out_reason: v.opt_out_reason } : {}),
        },
      }),
    onSettled: done,
  });
  // DOCS-ALWAYS-IN-MEMORY: every space is on by default; switching one off asks why (a modal)
  const [optOut, setOptOut] = useState<RecallKbSpace | null>(null);
  const [reason, setReason] = useState("");
  const syncNow = useMutation({
    mutationFn: (id: string) => api.client.recall.kbSyncNow({ params: { co, id } }),
    onSettled: done,
  });
  if (q.isPending) return <Loading label="Loading knowledge bases" rows={2} />;
  if (q.isError) return <LoadError what="knowledge bases" error={q.error} />;
  const { spaces, interval_minutes: interval } = q.data;
  const on = spaces.filter((s) => s.sync_enabled).length;
  return (
    <SettingsGroup
      title="Docs index in Recall"
      count={on}
      data-slot="recall-kb"
      actions={
        <span className="text-xs text-muted-foreground">Synced at most every {interval} min</span>
      }
    >
      <p className="px-4 py-3 text-[13px] text-muted-foreground" data-slot="recall-kb-note">
        Docs live in CompanyOS, and agents read them there. This is an optional search copy of a
        space's docs in Recall; switching it off changes nothing agents can read.
      </p>
      {spaces.length === 0 ? (
        <p className="px-4 py-3 text-[13px] text-muted-foreground">No spaces yet.</p>
      ) : (
        spaces.map((k) => (
          <SettingsRow
            key={k.space_id}
            data-kb-space={k.space_id}
            label={
              <span className="flex items-center gap-2">
                {k.name}
                {k.slug ? (
                  <code className="text-[11px] font-normal text-muted-foreground">{k.slug}</code>
                ) : null}
              </span>
            }
            hint={
              <>
                {kbHint(k, interval)}
                {k.last_error ? (
                  <span className="mt-0.5 block text-destructive" data-slot="recall-kb-error">
                    {k.last_error_at ? `${formatDateTime(k.last_error_at)}: ` : ""}
                    {k.last_error}
                  </span>
                ) : null}
              </>
            }
          >
            <NativeSelect
              size="sm"
              aria-label={`Agent access for ${k.name}`}
              value={k.agent_access}
              disabled={update.isPending}
              onChange={(e) =>
                update.mutate({
                  id: k.space_id,
                  sync_enabled: k.sync_enabled,
                  agent_access: e.target.value as RecallKbSpace["agent_access"],
                })
              }
            >
              {(Object.keys(ACCESS) as RecallKbSpace["agent_access"][]).map((a) => (
                <NativeSelectOption key={a} value={a}>
                  {ACCESS[a]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <Button
              size="sm"
              variant="outline"
              disabled={!k.sync_enabled || k.sync_requested || syncNow.isPending}
              onClick={() => syncNow.mutate(k.space_id)}
            >
              Sync now
            </Button>
            <Switch
              aria-label={`Sync ${k.name} to Recall`}
              checked={k.sync_enabled}
              disabled={update.isPending}
              onCheckedChange={(v) => {
                if (v)
                  update.mutate({
                    id: k.space_id,
                    sync_enabled: true,
                    agent_access: k.agent_access,
                  });
                else {
                  setReason("");
                  setOptOut(k);
                }
              }}
            />
          </SettingsRow>
        ))
      )}
      <Dialog open={optOut !== null} onOpenChange={(o) => (o ? null : setOptOut(null))}>
        <DialogContent data-slot="recall-kb-opt-out">
          <DialogHeader>
            <DialogTitle>Stop indexing {optOut?.name} in Recall</DialogTitle>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="recall-kb-opt-out-reason">Reason</FieldLabel>
            <Textarea
              id="recall-kb-opt-out-reason"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
            <Button
              disabled={!reason.trim() || update.isPending}
              onClick={() => {
                if (!optOut) return;
                update.mutate(
                  {
                    id: optOut.space_id,
                    sync_enabled: false,
                    agent_access: optOut.agent_access,
                    opt_out_reason: reason.trim(),
                  },
                  { onSuccess: () => setOptOut(null) },
                );
              }}
            >
              Switch off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsGroup>
  );
}

/**
 * D15: "Agents write memory selectively". On by default (MEMORY-HYGIENE); it lives in
 * `settings.memory.agent_discipline` and is saved with `PATCH /companies/{co}` (the other settings
 * kept as they are). On: agents write only typed one-sentence memories with a why and evidence;
 * MEMORY-SKILL-2: no per-run cap (the old per-run select is gone; a stored value is dropped on
 * the next save), duplicates refused; a lesson is the agent's own until a person shares it; run
 * summaries and agents' own messages stay out of Recall.
 */
function AgentDiscipline({ api, co }: { api: Api; co: string }) {
  const qc = useQueryClient();
  const q = useQuery(companyQuery(api, co));
  const save = useMutation({
    mutationFn: (next: { enabled: boolean }) => {
      const settings = q.data?.settings ?? {};
      return api.client.companies.update({
        params: { co },
        body: {
          settings: {
            ...settings,
            memory: { ...settings.memory, agent_discipline: next },
          },
        },
      });
    },
    onSuccess: (detail) => qc.setQueryData(keys.company(co), detail),
  });
  if (q.isPending) return <Loading label="Loading memory settings" rows={1} />;
  if (q.isError) return <LoadError what="memory settings" error={q.error} />;
  const d = q.data.settings.memory?.agent_discipline;
  // MEMORY-HYGIENE: on by default; only an explicit `enabled: false` switches it off.
  const enabled = d?.enabled !== false;
  return (
    <SettingsGroup title="Agent memory writes" data-slot="recall-discipline">
      <SettingsRow
        label="Agents write memory selectively"
        hint={
          enabled
            ? "On. Agents save decisions, lessons, facts, patterns and corrections, one sentence each (max 280 characters) with a why and evidence. No count limit; duplicates are refused. A lesson stays the agent's own until its supervisor shares it. Run summaries and what agents post or change stay out of Recall."
            : "Off: agents write memory freely, including long status reports. Not recommended."
        }
      >
        <Switch
          aria-label="Agents write memory selectively"
          checked={enabled}
          disabled={save.isPending}
          onCheckedChange={(v) => save.mutate({ enabled: v })}
        />
      </SettingsRow>
      {save.isError ? (
        <p role="alert" className="px-4 py-2 text-[13px] text-destructive">
          {problemText(save.error, "The memory setting was not saved. Try again.")}
        </p>
      ) : null}
    </SettingsGroup>
  );
}

/** A fact: label on the left, its value on the right in 13 px. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <SettingsRow label={label}>
      <span className="min-w-0 text-[13px] text-foreground tabular-nums">{children}</span>
    </SettingsRow>
  );
}

/** RECALL-FOLLOWUP: webhook or polling, and how the last poll went. */
function eventsBadge(e: RecallStatus["events"]): [string, "success" | "warning" | "neutral"] {
  if (e.mode === "webhook") return ["Webhook", "success"];
  if (e.mode === "off") return ["Not connected", "neutral"];
  if (e.state === "ok") return ["Polling (install feed)", "success"];
  if (e.state === "waiting") return ["Polling (install feed) · starting", "neutral"];
  return [e.state === "refused" ? "Polling · refused" : "Polling · error", "warning"];
}

function RecallStatusView({ status: s }: { status: RecallStatus }) {
  const [label, tone] = CONNECTION[s.recall.state];
  const [eventsLabel, eventsTone] = eventsBadge(s.events);
  return (
    <>
      <SettingsGroup
        title="Connection"
        data-slot="recall-connection"
        actions={
          <Badge variant={tone} data-recall-state={s.recall.state}>
            {label}
          </Badge>
        }
      >
        {s.recall.state === "connected" ? (
          <>
            <Fact label="Members linked">{s.members}</Fact>
            {s.members > 0 ? (
              <Fact label="Member accounts">
                {`${s.member_identity.global} on their own Recall account, ${s.member_identity.local} on a company-only account`}
              </Fact>
            ) : null}
            <Fact label="Memory scopes">{s.buckets}</Fact>
            <Fact label="Last full sync">
              {s.last_reconciled_at ? formatDateTime(s.last_reconciled_at) : "Not yet"}
            </Fact>
          </>
        ) : (
          <SettingsRow
            data-slot="recall-not-connected"
            label={label}
            hint={
              <>
                {s.recall.detail}
                {s.recall.state === "not_configured"
                  ? " Until then memory search and new proposals are off."
                  : null}
              </>
            }
          />
        )}
        <SettingsRow
          data-slot="recall-person-enforcement"
          data-enforced={s.person_enforcement.enforced ? "on" : "off"}
          label={s.person_enforcement.label}
          hint={
            s.person_enforcement.enforced
              ? "Recall checks every person's memory read against their own access."
              : "People's memory search is limited by CompanyOS's own access rules; Recall does not check each person until the operator registers the signing key."
          }
        />
      </SettingsGroup>

      <SettingsGroup title="Company tenant" data-slot="recall-tenant">
        {s.tenant ? (
          <>
            <Fact label="Tenant">
              <code className="text-xs break-all">{s.tenant.recall_tenant_id}</code>
            </Fact>
            <Fact label="Slug">{s.tenant.slug ?? "—"}</Fact>
            <Fact label="Install token">
              {s.tenant.has_install_key ? KEY_STATE[s.tenant.install_key_state] : "Not held"}
            </Fact>
          </>
        ) : (
          <SettingsRow
            label="Not provisioned"
            hint="Created within a minute of Recall being connected while the Memory add-on is on."
          />
        )}
      </SettingsGroup>

      <SettingsGroup
        title="Events from Recall"
        data-slot="recall-webhook"
        actions={<Badge variant={eventsTone}>{eventsLabel}</Badge>}
      >
        {s.events.mode === "webhook" ? (
          <>
            <Fact label="Delivery">Webhook (signed callbacks)</Fact>
            <SettingsRow label="Callback path">
              <code className="text-xs break-all">{s.webhook.callback_path}</code>
            </SettingsRow>
            <Fact label="Registered">
              {s.webhook.updated_at ? formatDateTime(s.webhook.updated_at) : "Not yet"}
            </Fact>
          </>
        ) : s.events.mode === "polling" ? (
          <>
            <SettingsRow
              label="Delivery"
              hint="No webhook is registered, so CompanyOS reads Recall's install event feed every minute with the company's install key."
            >
              <span className="text-[13px] text-foreground">Polling (install feed)</span>
            </SettingsRow>
            <Fact label="Last poll">
              {s.events.polled_at ? formatDateTime(s.events.polled_at) : "Not yet"}
            </Fact>
            <Fact label="Events read">{s.events.seen}</Fact>
            {s.events.state === "refused" || s.events.state === "error" ? (
              <Fact label="Last answer">{s.events.detail ?? "Refused"}</Fact>
            ) : null}
          </>
        ) : (
          <SettingsRow
            label="Not connected yet"
            hint="Events arrive once the company's tenant exists."
          />
        )}
      </SettingsGroup>

      <SettingsGroup title="Agent keys" count={s.agents.length} data-slot="recall-agents">
        {s.agents.length ? (
          s.agents.map((a) => (
            <SettingsRow
              key={`${a.subject_kind}:${a.subject_id}`}
              label={
                a.name ?? (a.subject_kind === "digital_employee" ? "A digital employee" : "Agent")
              }
              hint={`Updated ${formatDateTime(a.updated_at)}`}
            >
              <Badge variant={a.has_key && a.key_state === "acked" ? "success" : "neutral"}>
                {a.has_key ? KEY_STATE[a.key_state] : "Not held"}
              </Badge>
            </SettingsRow>
          ))
        ) : (
          <p className="px-4 py-3 text-[13px] text-muted-foreground">No agent keys yet.</p>
        )}
      </SettingsGroup>
    </>
  );
}
